import type { AssistantModuleContext } from '@/types/support-assistant';

export interface NavTab {
  label: string;
  href: string;
}

export interface NavModule {
  key: string;
  name: string;
  href: string;
}

const ROUTE_URL_MAX = 500;

/**
 * Longest-prefix wins: exactly one tab is active, the most specific match.
 * Shared by ModuleSubNav (highlight) and the assistant (page derivation).
 */
export function getActiveSubNavTab<T extends { href: string }>(
  tabs: T[],
  pathname: string,
): T | null {
  const matches = (href: string) =>
    pathname === href || pathname.startsWith(href + '/');
  return tabs
    .filter((tab) => matches(tab.href))
    .reduce<T | null>(
      (best, tab) => (best === null || tab.href.length > best.href.length ? tab : best),
      null,
    );
}

/**
 * pagePath is always the active tab href or the module landing href (both come
 * from the API navigation config), never the raw pathname.
 */
export function buildModuleContext(
  currentModule: NavModule | null | undefined,
  tabs: NavTab[],
  location: { pathname: string; search?: string },
): AssistantModuleContext | null {
  if (!currentModule) return null;
  const active = getActiveSubNavTab(tabs ?? [], location.pathname);
  const pagePath = active?.href ?? currentModule.href;
  const page = active?.label ?? currentModule.name;
  return {
    type: 'module',
    id: `${currentModule.key}:${pagePath}`,
    label: page,
    moduleKey: currentModule.key,
    module: currentModule.name,
    pagePath,
    page,
    routeUrl: `${location.pathname}${location.search ?? ''}`.slice(0, ROUTE_URL_MAX),
  };
}
